import React, { useEffect, useState } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import ShowPageWrapper from "../shows/show_page_wrapper";
import { fetchPublicShow } from "../../../action_managers/public_show";
import ExpandableText from "../../lib/expandable_text";
import HighlightBox from "../../lib/highlight-box";
import Divider from "../../lib/divider";
import { If } from "react-extras";
import BlueCheckmark from "../../lib/blue_checkmark/blue_checkmark";
import { formatMoney } from "../../../lib/format-money";
import { Button, Col, Row } from "react-bootstrap";
import facebook from "../../../icons/facebook.svg";
import instagram from "../../../icons/instagram.svg";
import twitter from "../../../icons/twitter.svg";
import youtube from "../../../icons/youtube.svg";
import ExternalLink from "../../lib/external_link";
import { getMegapodURL } from "../../../lib/config";
import AddToCampaignModal from "./add_to_campaign_modal";
import moment from "moment";
import { getYoutubeForShow } from "../../../action_managers/shows";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import { useComponentDidMount } from "../../../lib/hook";
import { Tabs, TabPanel, TabList, Tab } from "../../lib/tab";
import { TwoLineCell } from "../../lib/tables/styled-table";
import { formatDuration } from "../../../lib/duration";
import MediaPlayer from "../../lib/media_player";
import { getSampleAdReads } from "../../../action_managers/campaigns";
import ExclusiveTag from "../../lib/exclusive_tag";
import { CampaignStyleCampaignAudio } from "../../../constants/campaigns";
import { itunesCategoriesMap } from "../../../data/itunes_categories";

const Hosts = props => {
  const { hostNames = [] } = props;

  return (
    <div className="margin-bottom--small">
      <h3>Hosts</h3>
      <Divider smallMargin />

      {hostNames.map((name, i) => (
        <li key={i} className="list-no-style margin-bottom--small">
          <span style={{ fontSize: "18px" }}>
            {name.firstName} {name.lastName}
          </span>
        </li>
      ))}
    </div>
  );
};

const SocialMedia = props => {
  const { show = {} } = props;

  const channelsByShow = useSelector(state => state.youtube.channelsByShow);
  const channels = channelsByShow[show.uuid];
  const channel = head(channels);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEmpty(show)) {
      dispatch(getYoutubeForShow(show.uuid));
    }
  }, [show, dispatch]);

  const advertisementSettings = show?.advertisementSettings ?? {};
  let supportedSocialMedia = [
    { fieldName: "twitterLink", logo: twitter, label: "Twitter" },
    { fieldName: "instagramLink", logo: instagram, label: "Instagram" },
    { fieldName: "facebookLink", logo: facebook, label: "Facebook" },
    { fieldName: "youtubeLink", logo: youtube, label: "YouTube" },
  ].filter(({ fieldName }) => advertisementSettings[fieldName]);

  if (!supportedSocialMedia.length && !channel) {
    return null;
  }
  const hasUserProvidedYoutubeLink = supportedSocialMedia.find(
    ({ fieldName }) => fieldName === "youtubeLink"
  );
  return (
    <>
      <h3>Social Media</h3>
      <Divider smallMargin />

      {supportedSocialMedia.map(({ fieldName, logo, label }) => {
        return (
          <Col xs={12} md={6} className={"margin-bottom--small"} key={fieldName}>
            <Row>
              <ExternalLink className={"no-style-link"} href={advertisementSettings[fieldName]}>
                <div className="flex-row-container align-center">
                  <img src={logo} alt="logo" width={68} height={68} />
                  <div className="bold margin-left--large">{label}</div>
                </div>
              </ExternalLink>
            </Row>
          </Col>
        );
      })}
      {channel && !hasUserProvidedYoutubeLink && (
        <Col xs={12} md={6} className={"margin-bottom--small"}>
          <Row>
            <ExternalLink
              className={"no-style-link"}
              href={`https://www.youtube.com/channel/${channel.id}`}>
              <div className="flex-row-container align-center">
                <img src={youtube} alt="logo" width={68} height={68} />
                <div className="bold margin-left--large">YouTube</div>
              </div>
            </ExternalLink>
          </Row>
        </Col>
      )}
    </>
  );
};

const CPM = props => {
  const { advertisementSettings, show } = props;
  if (!advertisementSettings) return null;

  const hostReadCPMS = [
    { fieldName: "hostReadPreRollCPM", label: "PRE-ROLL" },
    { fieldName: "hostReadMidRollCPM", label: "MID-ROLL" },
    { fieldName: "hostReadPostRollCPM", label: "POST-ROLL" },
  ];
  const preRecordedCPMS = [
    { fieldName: "preRecordedPreRollCPM", label: "PRE-ROLL" },
    { fieldName: "preRecordedMidRollCPM", label: "MID-ROLL" },
    { fieldName: "preRecordedPostRollCPM", label: "POST-ROLL" },
  ];

  const RenderCPMs = ({ cpms, title }) => (
    <div>
      <div className="flex-row-container margin-bottom--small">
        <BlueCheckmark />
        <div className={"margin-left--small"}>{title}</div>
      </div>
      <Row>
        {cpms.map(({ fieldName, label }) => {
          return (
            <Col key={fieldName} xs={12} md={4}>
              <HighlightBox
                title={label}
                value={formatMoney(advertisementSettings[fieldName]) + " CPM"}
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );

  return (
    <>
      <h3>Advertising Information</h3>
      <Divider smallMargin />

      <h5 className="bold margin-top--large">Ad Types </h5>
      <If condition={advertisementSettings.isHostReadEnabled}>
        <RenderCPMs cpms={hostReadCPMS} title={"Host Read Ads"} />
      </If>
      <If condition={advertisementSettings.isPreRecordedEnabled}>
        <RenderCPMs cpms={preRecordedCPMS} title={"Pre-Recorded Ads"} />
      </If>
    </>
  );
};

const ShowInformation = props => {
  const { show } = props;

  const averageEpisodeDuration = moment.duration(show?.averageEpisodeDuration ?? 0).humanize();
  const estimatedEpisodeFrequency = show?.estimatedEpisodeFrequency;
  if (!averageEpisodeDuration || !estimatedEpisodeFrequency) {
    return null;
  }
  return (
    <>
      <h3>Show Information</h3>
      <Divider smallMargin />

      <Row>
        <If condition={!!show.estimatedWeeklyDownloads}>
          <Col xs={12} md={4}>
            <HighlightBox
              title={"Weekly Downloads"}
              value={show.estimatedWeeklyDownloads?.toLocaleString()}
            />
          </Col>
        </If>
        <Col xs={12} md={4}>
          <HighlightBox title={"Episode Frequency"} value={estimatedEpisodeFrequency} />
        </Col>

        <Col xs={12} md={4}>
          <HighlightBox title={"Avg. Episode Length"} value={averageEpisodeDuration} />
        </Col>
      </Row>
    </>
  );
};

const AdvertiserBrowseShowPage = props => {
  const { fetchPublicShow, publicShows, getSampleAdReads } = props;
  const { showUUID } = useParams();
  const isLoading = useSelector(state => state.publicShows.isLoading);
  const [showModal, setShowModal] = useState(false);
  const sampleAdReads = useSelector(state => state.publicShows.adReadsByShowUUID?.[showUUID]);

  useComponentDidMount(() => {
    fetchPublicShow(showUUID);
    getSampleAdReads(showUUID);
  });

  const show = publicShows[showUUID] ?? {};
  const advertisementSettings = show?.advertisementSettings ?? {};
  const categoryNames = show?.categoryUUIDs?.map(uuid => itunesCategoriesMap[uuid]?.name);
  return (
    <>
      <If condition={!isLoading}>
        <ShowPageWrapper
          backLink={"/browse"}
          show={show}
          eyebrow={
            <div className={"m-bxxs"}>
              <ExclusiveTag>{categoryNames?.slice(0, 1)}</ExclusiveTag>
            </div>
          }
          title={show.title}
          subtitle={
            <span className="small">
              <span className="bold">Categories: </span>
              {categoryNames?.join(", ")}
            </span>
          }
          underAlbumContent={() => {
            return (
              <div style={{ width: "240px" }}>
                <Button className="btn-primary width-100" onClick={() => setShowModal(true)}>
                  Add to Campaign
                </Button>
                <div className={"text-center"}>
                  <ExternalLink href={`${getMegapodURL()}shows/${show.uuid}`}>
                    Public Page
                  </ExternalLink>
                </div>
              </div>
            );
          }}>
          <ExpandableText dangerousText={show.description} />
          <Tabs>
            <TabList>
              <Tab>About</Tab>
              <Tab>Episodes</Tab>
            </TabList>
            <TabPanel>
              <div>
                <div className={"m-vm"}>
                  <Hosts hostNames={advertisementSettings?.hostNames} />
                </div>
                <div className={"m-vm"}>
                  <ShowInformation show={show} />
                </div>
                <div className={"m-vm"}>
                  <CPM advertisementSettings={advertisementSettings} show={show} />
                </div>
                <div className={"m-vm"}>
                  <SocialMedia show={show} />
                </div>
                <div className={"m-vm"}>
                  <SampleAdReads sampleAdReads={sampleAdReads} />
                </div>
              </div>
            </TabPanel>
            <TabPanel>{<EpisodeTable episodes={show?.episodes} />}</TabPanel>
          </Tabs>
        </ShowPageWrapper>
      </If>
      <If condition={showModal}>
        <AddToCampaignModal close={() => setShowModal(false)} show={show} />
      </If>
    </>
  );
};

const SampleAdReads = ({ sampleAdReads }) => {
  if (!sampleAdReads?.length) return null;

  return (
    <Row>
      <Col xs={12} className={"m-tm"}>
        <h3>Past Campaigns + Example Reads</h3>
        <Divider smallMargin />
        {sampleAdReads.map((adRead, i) => {
          const endTime = moment.unix(adRead.estimatedEndsAt);
          let endsAtText = `COMPLETED ON: ${endTime.format("ll")}`;
          let exclusiveTag = null;
          if (moment().isBefore(endTime)) {
            exclusiveTag = (
              <div style={{ flexBasis: "50px" }} className={""}>
                <ExclusiveTag>Live</ExclusiveTag>
              </div>
            );
            endsAtText = `EST END DATE: ${endTime.format("ll")}`;
          }
          return (
            <div
              key={i}
              className={"p-vxxs flex-row-container align-center"}
              style={{ borderBottom: "1px solid rgba(black, .1)" }}>
              <div>
                <TwoLineCell title={adRead.campaignName} subtitle={adRead.productName} />
              </div>
              <div className="flex-1"></div>
              {exclusiveTag}
              <div className={"small bold m-rxs"} style={{ width: 160, textAlign: "right" }}>
                {endsAtText}
              </div>
              <div style={{ width: 38 }}>
                <MediaPlayer
                  disabled={adRead.style === CampaignStyleCampaignAudio}
                  infoBitText={
                    adRead.style === CampaignStyleCampaignAudio ? "PRE-RECORDED AD" : null
                  }
                  mini={true}
                  mediaFileUUID={adRead.mediaFileUUID}
                />
              </div>
            </div>
          );
        })}
      </Col>
    </Row>
  );
};

const EpisodeTable = ({ episodes }) => {
  const [showAll, setShowAll] = useState(false);
  if (!episodes) return null;
  const originalLength = episodes.length;
  if (!showAll) {
    episodes = episodes.slice(0, 5);
  }
  return (
    <div>
      {episodes.map(episode => (
        <div
          className={"flex-row-container p-txxs align-center"}
          key={episode.uuid}
          style={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
          <div className="flex-1">
            <TwoLineCell
              title={episode.title}
              subtitle={`${moment.unix(episode.publishedAt).format("ll")} - ${formatDuration(
                episode.duration
              )}`}
            />
          </div>

          <div className={"flex-1"}>
            <MediaPlayer mediaFileURL={episode.streamURL} />
          </div>
        </div>
      ))}
      {!showAll && (
        <div className={"text-center m-txs"}>
          <button className="fake-link" onClick={() => setShowAll(true)}>
            View All ({originalLength})
          </button>
        </div>
      )}
    </div>
  );
};

export default connect(state => ({ publicShows: state.publicShows }), {
  fetchPublicShow,
  getSampleAdReads,
})(AdvertiserBrowseShowPage);
